<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import AppHeader from "~/components/header/AppHeader.vue";
import HeroHeader from "~/components/HeroHeader.vue";
import CardRail from "~/components/card-rail/CardRail.vue";
import FooterMainPage from "~/components/FooterMainPage.vue";
import PrivacyPreferencesModal from "~/components/PrivacyPreferencesModal.vue";
import EstablishmentCard from "~/components/cards/EstablishmentCard.vue";
import { formatLocalDate } from "~/composables/useDateTime";
import { useGeolocation } from "~/composables/useGeolocation";
import { useResponsiveLimit } from "~/composables/useResponsiveLimit";
import { useLocationStore } from "~/stores/location";
import {
  createCookieConsentState,
  COOKIE_CONSENT_KEY,
  COOKIE_CONSENT_OPTIONS,
  normalizeCookieConsent,
  type CookieConsentValue,
} from "~/utils/privacyConsent";
import type { UiCard } from "~~/shared/models/cards";
import { EstablishmentService } from "~~/shared/services/establishment";
import { type ApiClientOptions, ApiClient } from "~~/shared/api/ApiClient";
import {
  useApiBaseUrl,
  type CategoryResponse,
  type EventsCategoryResponse,
} from "~~/shared/models";
import { EventsService } from "~~/shared/services/events";
import { setResponseHeader } from "h3";

const { t } = useI18n();
const { emit } = useAnalytics();
const { translateCategory } = useCategoryTranslation();

const geo = useGeolocation();
const locationStore = useLocationStore();
const route = useRoute();
const localePath = useLocalePath();
const { APP_URL, SITE_URL } = usePublicUrls();
const homepagePath = localePath("/");
const homepageUrl = `${SITE_URL}${homepagePath === "/" ? "" : homepagePath}`;
const HOME_DISCOVERY_LIMIT = 4;
const homeStartDate = formatLocalDate(new Date());

type HomeEventsResponse = { events?: UiCard[]; hasMore?: boolean };
type HomeEstablishmentsResponse = {
  establishments?: UiCard[];
  hasMore?: boolean;
};
const {
  data: homeDiscovery,
  pending: homeDiscoveryPending,
  error: homeDiscoveryError,
} = await useAsyncData("home-public-discovery", async () => {
  const [eventsResponse, establishmentsResponse] = await Promise.all([
    $fetch<HomeEventsResponse>("/bff/events", {
      query: {
        page: 1,
        limit: HOME_DISCOVERY_LIMIT,
        startDate: homeStartDate,
        startTime: "00:00",
      },
    }),
    $fetch<HomeEstablishmentsResponse>("/bff/establishments", {
      query: { page: 1, limit: HOME_DISCOVERY_LIMIT },
    }),
  ]);

  return {
    events: (eventsResponse?.events ?? []) as UiCard[],
    establishments: (establishmentsResponse?.establishments ?? []) as UiCard[],
    eventsHaveMore: Boolean(eventsResponse?.hasMore),
    establishmentsHaveMore: Boolean(establishmentsResponse?.hasMore),
  };
});
if (homeDiscoveryError.value) {
  if (import.meta.server) {
    const requestEvent = useRequestEvent();
    if (requestEvent)
      setResponseHeader(requestEvent, "Cache-Control", "no-store");
  }
  throw createError({
    statusCode: 503,
    statusMessage: "Public discovery unavailable",
    fatal: true,
    cause: homeDiscoveryError.value,
  });
}
const defaultEvents = computed(() => homeDiscovery.value?.events ?? []);
const defaultEstablishments = computed(
  () => homeDiscovery.value?.establishments ?? [],
);
// simple local error state used by the rails
const eventsError = ref<unknown>(null);
const upfestEventError = ref<unknown>(null);
const upfestEstablishmentError = ref<unknown>(null);
const categoriesLoading = ref(true);
const nearFirstLoadPending = ref(true);

type Mode = "event" | "establishment";

const mode = ref<Mode>("event");
const upfestError = computed(() =>
  mode.value === "event"
    ? upfestEventError.value
    : upfestEstablishmentError.value,
);
const heroLocation = ref("");
const heroMiddle = ref("");
const heroDate = ref("");
const heroLat = ref<number | null>(null);
const heroLon = ref<number | null>(null);
const heroCountry = ref<string>("");
const heroAddress = ref<Record<string, unknown> | null>(null);
const heroRadiusKm = ref<number>(100);
const eventOptions = ref<Array<{ label: string; value: string }>>([]);
const establishmentOptions = ref<Array<{ label: string; value: string }>>([]);

// Read mode from query parameter — reactive so footer/external navigation updates work too
watch(
  () => route.query.mode,
  (queryMode) => {
    if (queryMode === "event" || queryMode === "establishment") {
      mode.value = queryMode as Mode;
    }
  },
  { immediate: true },
);

// Location prompt / detection (moved to Pinia store)
const locationPermission = computed(() => locationStore.permission);
const cookieConsent = useCookie<CookieConsentValue>(
  COOKIE_CONSENT_KEY,
  COOKIE_CONSENT_OPTIONS,
);
const resolvedCookieConsent = computed(() =>
  normalizeCookieConsent(cookieConsent.value),
);
const analyticsEnabled = ref(false);
const locationEnabled = ref(true);
const showPrivacyPreferencesModal = computed(
  () =>
    clientReady.value &&
    (locationPermission.value === null || resolvedCookieConsent.value === null),
);
const clientReady = ref(false);
const hasMounted = ref(false);
const consentToastVisible = ref(false);
const consentToastMessage = ref("");
let consentToastTimer: ReturnType<typeof setTimeout> | null = null;

function showConsentToast(message: string) {
  consentToastMessage.value = message;
  consentToastVisible.value = true;

  if (consentToastTimer) {
    clearTimeout(consentToastTimer);
  }

  consentToastTimer = setTimeout(() => {
    consentToastVisible.value = false;
  }, 4500);
}

watch(
  resolvedCookieConsent,
  (value) => {
    analyticsEnabled.value = value?.analytics ?? false;
  },
  { immediate: true },
);

watch(
  () => locationStore.permission,
  (permission) => {
    locationEnabled.value =
      permission === null ? true : permission === "allowed";
  },
  { immediate: true },
);

onMounted(() => {
  clientReady.value = true;
  locationStore.init();
  void syncLocationFromBrowserPermission();

  if (locationStore.approxRegion) {
    heroLocation.value = locationStore.approxRegion;
    heroLat.value = locationStore.latitude;
    heroLon.value = locationStore.longitude;
    heroCountry.value = locationStore.country;
  }

  if (!heroDate.value) {
    heroDate.value = formatLocalDate(new Date());
  }

  void loadCategories();
  reloadNearbyRail();
  hasMounted.value = true;
});

onBeforeUnmount(() => {
  if (consentToastTimer) {
    clearTimeout(consentToastTimer);
  }
  nearbyEventsAc?.abort();
  upfestEventsAc?.abort();
  nearbyEstAc?.abort();
  upfestEstAc?.abort();
});
const opts: ApiClientOptions = {
  baseUrl: useApiBaseUrl(),
};
const client: ApiClient = new ApiClient(opts);
const establishmentService = new EstablishmentService(client);
const eventService = new EventsService(client);
const estCategories = ref<Array<CategoryResponse>>([]);
const eventCategories = ref<Array<EventsCategoryResponse>>([]);

const loadCategories = async () => {
  categoriesLoading.value = true;
  try {
    const [estRes, evtRes] = await Promise.all([
      establishmentService.listCategories(),
      eventService.listCategories(),
    ]);
    estCategories.value = estRes.categories || [];
    eventCategories.value = evtRes.categories || [];

    eventOptions.value =
      eventCategories.value?.map((cat) => ({
        label: translateCategory(cat.name),
        value: cat.id,
      })) || [];
    establishmentOptions.value =
      estCategories.value?.map((cat) => ({
        label: translateCategory(cat.name),
        value: cat.id,
      })) || [];
  } finally {
    categoriesLoading.value = false;
  }
};

const savePrivacyPreferences = async () => {
  cookieConsent.value = createCookieConsentState(analyticsEnabled.value);
  useNuxtApp().$syncPostHogConsent();

  if (locationEnabled.value) {
    const locationGranted = await requestGeolocation();
    if (!locationGranted) {
      showConsentToast(t("privacyPreferences.savedWithoutLocation"));
    }
    return;
  }

  await declineLocation();
};

const acceptAllPrivacyPreferences = async () => {
  analyticsEnabled.value = true;
  locationEnabled.value = true;
  await savePrivacyPreferences();
};

const keepOnlyNecessaryPrivacyPreferences = async () => {
  analyticsEnabled.value = false;
  locationEnabled.value = false;
  await savePrivacyPreferences();
};

const syncLocationFromBrowserPermission = async () => {
  await locationStore.syncBrowserPermission();

  if (locationStore.permission === "allowed" && !locationStore.approxRegion) {
    await requestGeolocation();
    return;
  }
};

const requestGeolocation = async (): Promise<boolean> => {
  await geo.requestGeolocation({ fallbackToIp: false });

  heroLocation.value = geo.approxRegion.value || "";
  heroLat.value = locationStore.latitude;
  heroLon.value = locationStore.longitude;
  heroCountry.value = locationStore.country;
  return locationStore.permission === "allowed";
};

const declineLocation = async () => {
  await geo.declineLocation();
  heroLocation.value = geo.approxRegion.value || "";
  heroLat.value = locationStore.latitude;
  heroLon.value = locationStore.longitude;
  heroCountry.value = locationStore.country;
};

// keep hero updated when store location changes, and refresh nearby rail
watch(
  () => locationStore.approxRegion,
  (v) => {
    if (!hasMounted.value) return;

    heroLocation.value = v || "";
    heroLat.value = locationStore.latitude;
    heroLon.value = locationStore.longitude;
    heroCountry.value = locationStore.country;

    if (v) {
      if (mode.value === "event") void loadNearbyEvents(1);
      else void loadNearbyEstablishments(1);
      return;
    }

    reloadNearbyRail();
  },
);

const onHeroSearch = () => {
  const properties = {
    source: "home",
    mode: mode.value,
    has_location: heroLat.value !== null && heroLon.value !== null,
    has_date: mode.value === "event" && Boolean(heroDate.value),
    category_ids: heroMiddle.value.split(",").filter(Boolean),
    scope: "upfest",
  };
  emit("search_submitted", properties);
  reloadRails(properties);
};

const onModeChange = (next: Mode) => {
  mode.value = next;
  // preserve heroLocation and selected date across mode switches
  heroMiddle.value = "";
};

// Sync mode to URL whenever it changes
watch(
  () => mode.value,
  (newMode) => {
    if (import.meta.client) {
      const router = useRouter();
      router.replace({ query: { ...route.query, mode: newMode } });
    }
  },
);

const onHeaderSearchClick = () => {};
const onHeaderMenuClick = () => {};
const onHeaderLogin = () => {
  window.location.href = `${APP_URL}/`;
};
const onHowItWorks = () => {};

type HeroLocationSelection = {
  location: string;
  lat: number;
  lon: number;
  city: string;
  country: string;
  address: Record<string, unknown>;
  radiusKm: number;
};

function setHeroLocation(selection: HeroLocationSelection) {
  heroLocation.value = selection.location;
  heroLat.value = selection.lat;
  heroLon.value = selection.lon;
  heroCountry.value = selection.country;
  heroAddress.value = selection.address;
  heroRadiusKm.value = selection.radiusKm;
  locationStore.setLocation({
    approxRegion: selection.location,
    latitude: selection.lat,
    longitude: selection.lon,
    city: selection.city,
    country: selection.country,
    source: "manual",
    accuracy: null,
  });
}

const nearRailTitle = computed(() =>
  mode.value === "event" ? t("home.eventsNearYou") : t("home.placesNearYou"),
);
const upfestRailTitle = computed(() =>
  mode.value === "event" ? t("home.eventsOnUpfest") : t("home.placesOnUpfest"),
);
const nearItems = computed(() =>
  mode.value === "event"
    ? displayedEvents.value
    : displayedEstablishments.value,
);
const upfestItems = computed(() => {
  if (mode.value === "event") {
    return upfestEventsLoaded.value ? upfestEvents.value : defaultEvents.value;
  }
  return upfestEstablishmentsLoaded.value
    ? upfestEstablishments.value
    : defaultEstablishments.value;
});
const nearHasMore = computed(() =>
  mode.value === "event" ? eventHasMore.value : establishmentHasMore.value,
);
const upfestHasMore = computed(() => {
  if (mode.value === "event") {
    return upfestEventsLoaded.value
      ? upfestEventHasMore.value
      : Boolean(homeDiscovery.value?.eventsHaveMore);
  }
  return upfestEstablishmentsLoaded.value
    ? upfestEstablishmentHasMore.value
    : Boolean(homeDiscovery.value?.establishmentsHaveMore);
});
const nearLoading = computed(() =>
  mode.value === "event"
    ? eventLoadingMore.value
    : establishmentLoadingMore.value,
);
const upfestLoading = computed(() =>
  mode.value === "event"
    ? upfestEventLoadingMore.value
    : upfestEstablishmentLoadingMore.value,
);
const nearRailLoading = computed(
  () => nearLoading.value || nearFirstLoadPending.value,
);
const upfestRailLoading = computed(
  () => upfestLoading.value || homeDiscoveryPending.value,
);
const upfestSeeAllTo = computed(() => {
  const today = formatLocalDate(new Date());
  const hasModeFilters =
    Boolean(heroMiddle.value) ||
    (mode.value === "event" &&
      Boolean(heroDate.value) &&
      heroDate.value !== today);
  if (hasModeFilters) return undefined;
  return localePath(mode.value === "event" ? "/events" : "/venues");
});

const onNearLoadMore = () => {
  if (mode.value === "event") {
    if (!eventLoadingMore.value && eventHasMore.value)
      void loadNearbyEvents(eventPage.value + 1);
  } else {
    if (!establishmentLoadingMore.value && establishmentHasMore.value)
      void loadNearbyEstablishments(establishmentPage.value + 1);
  }
};

const onUpfestLoadMore = () => {
  if (mode.value === "event") {
    if (upfestEventLoadingMore.value || !upfestHasMore.value) return;
    void loadUpfestEvents(upfestEventPage.value + 1);
    return;
  }

  if (upfestEstablishmentLoadingMore.value || !upfestHasMore.value) return;
  void loadUpfestEstablishments(upfestEstablishmentPage.value + 1);
};

// User-driven mode changes refresh both discovery scopes.
watch(
  () => mode.value,
  () => {
    if (hasMounted.value) reloadRails();
  },
);

// Nearby events pagination
const eventPage = ref(1);
const eventLimit = useResponsiveLimit();
const eventHasMore = ref(false);
const eventLoadingMore = ref(false);
const displayedEvents = ref<UiCard[]>([]);

// Global events use the SSR response until the user changes mode or criteria.
const upfestEvents = ref<UiCard[]>([]);
const upfestEventsLoaded = ref(false);
const upfestEventPage = ref(1);
const upfestEventHasMore = ref(false);
const upfestEventLoadingMore = ref(false);

// Nearby establishments pagination
const establishmentPage = ref(1);
const establishmentLimit = useResponsiveLimit();
const establishmentHasMore = ref(false);
const establishmentLoadingMore = ref(false);
const displayedEstablishments = ref<UiCard[]>([]);

// Global establishments use the SSR response until the user changes mode or criteria.
const upfestEstablishments = ref<UiCard[]>([]);
const upfestEstablishmentsLoaded = ref(false);
const upfestEstablishmentPage = ref(1);
const upfestEstablishmentHasMore = ref(false);
const upfestEstablishmentLoadingMore = ref(false);

watch(eventLimit, (nextLimit, previousLimit) => {
  if (
    !hasMounted.value ||
    mode.value !== "event" ||
    nextLimit === previousLimit
  ) {
    return;
  }
  reloadNearbyRail();
});

watch(establishmentLimit, (nextLimit, previousLimit) => {
  if (
    !hasMounted.value ||
    mode.value !== "establishment" ||
    nextLimit === previousLimit
  ) {
    return;
  }
  reloadNearbyRail();
});

// AbortControllers for request cancellation
let nearbyEventsAc: AbortController | null = null;
let upfestEventsAc: AbortController | null = null;
let nearbyEstAc: AbortController | null = null;
let upfestEstAc: AbortController | null = null;

function reloadNearbyRail() {
  nearFirstLoadPending.value = true;
  if (mode.value === "event") {
    void loadNearbyEvents(1);
  } else {
    void loadNearbyEstablishments(1);
  }
}

let pendingSearch: Record<string, unknown> | undefined;
function reloadRails(search?: Record<string, unknown>) {
  pendingSearch = search;
  reloadNearbyRail();
  if (mode.value === "event") {
    nearbyEstAc?.abort();
    upfestEstAc?.abort();
    establishmentLoadingMore.value = false;
    upfestEstablishmentLoadingMore.value = false;
    upfestEventError.value = null;
    void loadUpfestEvents(1);
  } else {
    nearbyEventsAc?.abort();
    upfestEventsAc?.abort();
    eventLoadingMore.value = false;
    upfestEventLoadingMore.value = false;
    upfestEstablishmentError.value = null;
    void loadUpfestEstablishments(1);
  }
}

// ────────────────── HELPER: build event query params ──────────────────
function buildEventParams(
  page: number,
  limit: number,
  opts?: { geo?: boolean },
) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (heroDate.value) {
    params.set("startDate", heroDate.value);
    params.set("startTime", "00:00");
  } else {
    params.set("startDate", formatLocalDate(new Date()));
    params.set("startTime", "00:00");
  }
  if (opts?.geo && heroLat.value !== null && heroLon.value !== null) {
    params.set("latitude", String(heroLat.value));
    params.set("longitude", String(heroLon.value));
    params.set("radius", String(heroRadiusKm.value * 1000));
  }

  if (heroMiddle.value) {
    heroMiddle.value
      .split(",")
      .filter(Boolean)
      .forEach((cat) => params.append("categoryId", cat));
  }

  return params;
}

// ────────────────── HELPER: build establishment query params ──────────────────
function buildEstablishmentParams(
  page: number,
  limit: number,
  opts?: { geo?: boolean },
) {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (opts?.geo && heroLat.value !== null && heroLon.value !== null) {
    params.set("latitude", String(heroLat.value));
    params.set("longitude", String(heroLon.value));
    params.set("radius", String(heroRadiusKm.value * 1000));
  }

  if (heroMiddle.value) {
    heroMiddle.value
      .split(",")
      .filter(Boolean)
      .forEach((cat) => params.append("categoryId", cat));
  }

  return params;
}

// ────────────────── NEARBY EVENTS (geo-filtered) ──────────────────
async function loadNearbyEvents(page = 1) {
  // Skip nearby if we have no coordinates
  if (heroLat.value === null || heroLon.value === null) {
    displayedEvents.value = [];
    eventHasMore.value = false;
    if (page === 1) nearFirstLoadPending.value = false;
    return;
  }

  nearbyEventsAc?.abort();
  const ac = (nearbyEventsAc = new AbortController());

  try {
    eventLoadingMore.value = true;
    if (page === 1) {
      eventsError.value = null;
    }

    const params = buildEventParams(page, eventLimit.value, { geo: true });
    const res = await $fetch<Record<string, unknown>>(
      `/bff/events?${params.toString()}`,
      {
        signal: ac.signal,
      },
    );
    const cards: UiCard[] = ((res as { events?: UiCard[] } | undefined)
      ?.events ?? []) as UiCard[];

    if (page === 1) displayedEvents.value = cards;
    else {
      const existingIds = new Set(displayedEvents.value.map((c) => c.id));
      displayedEvents.value = [
        ...displayedEvents.value,
        ...cards.filter((c) => !existingIds.has(c.id)),
      ];
    }

    eventPage.value = page;
    eventHasMore.value =
      typeof (res as { hasMore?: boolean } | undefined)?.hasMore === "boolean"
        ? (res as { hasMore?: boolean }).hasMore === true
        : cards.length === eventLimit.value;
  } catch (err) {
    if (ac.signal.aborted) return;
    console.error("loadNearbyEvents error", err);
    if (page === 1) eventsError.value = err;
  } finally {
    if (!ac.signal.aborted) {
      eventLoadingMore.value = false;
      if (page === 1) nearFirstLoadPending.value = false;
    }
  }
}

async function loadUpfestEvents(page = 1) {
  const search = pendingSearch;
  upfestEventsAc?.abort();
  const ac = (upfestEventsAc = new AbortController());

  try {
    upfestEventLoadingMore.value = true;
    upfestEventError.value = null;
    if (page === 1) {
      upfestEventPage.value = 1;
      upfestEventHasMore.value = false;
    }

    const shouldRebase = page === 2 && !upfestEventsLoaded.value;
    const responses = await Promise.all(
      (shouldRebase ? [1, 2] : [page]).map((targetPage) => {
        const params = buildEventParams(targetPage, HOME_DISCOVERY_LIMIT);
        return $fetch<HomeEventsResponse>(`/bff/events?${params.toString()}`, {
          signal: ac.signal,
        });
      }),
    );
    const response = responses.at(-1);
    const cards = (response?.events ?? []) as UiCard[];

    if (shouldRebase) {
      const firstPageCards = (responses[0]?.events ?? []) as UiCard[];
      const firstPageIds = new Set(firstPageCards.map((card) => card.id));
      upfestEvents.value = [
        ...firstPageCards,
        ...cards.filter((card) => !firstPageIds.has(card.id)),
      ];
    } else if (page === 1) {
      upfestEvents.value = cards;
    } else {
      const existingIds = new Set(upfestEvents.value.map((card) => card.id));
      upfestEvents.value = [
        ...upfestEvents.value,
        ...cards.filter((card) => !existingIds.has(card.id)),
      ];
    }

    upfestEventsLoaded.value = true;
    upfestEventPage.value = page;
    upfestEventHasMore.value = Boolean(response?.hasMore);
    if (
      page === 1 &&
      search &&
      search === pendingSearch &&
      !ac.signal.aborted
    ) {
      emit("search_results_loaded", { ...search, result_count: cards.length });
      pendingSearch = undefined;
    }
  } catch (error) {
    if (ac.signal.aborted) return;
    console.error("loadUpfestEvents error", error);
    upfestEventError.value = error;
  } finally {
    if (!ac.signal.aborted) upfestEventLoadingMore.value = false;
  }
}

// ────────────────── NEARBY ESTABLISHMENTS (geo-filtered) ──────────────────
async function loadNearbyEstablishments(page = 1) {
  if (heroLat.value === null || heroLon.value === null) {
    displayedEstablishments.value = [];
    establishmentHasMore.value = false;
    if (page === 1) nearFirstLoadPending.value = false;
    return;
  }

  nearbyEstAc?.abort();
  const ac = (nearbyEstAc = new AbortController());

  try {
    establishmentLoadingMore.value = true;

    const params = buildEstablishmentParams(page, establishmentLimit.value, {
      geo: true,
    });
    const res = await $fetch<Record<string, unknown>>(
      `/bff/establishments?${params.toString()}`,
      {
        signal: ac.signal,
      },
    );
    const cards: UiCard[] = ((res as { establishments?: UiCard[] } | undefined)
      ?.establishments ?? []) as UiCard[];

    if (page === 1) displayedEstablishments.value = cards;
    else {
      const existingIds = new Set(
        displayedEstablishments.value.map((card) => card.id),
      );
      displayedEstablishments.value = [
        ...displayedEstablishments.value,
        ...cards.filter((card) => !existingIds.has(card.id)),
      ];
    }

    establishmentPage.value = page;
    establishmentHasMore.value =
      typeof (res as { hasMore?: boolean } | undefined)?.hasMore === "boolean"
        ? (res as { hasMore?: boolean }).hasMore === true
        : cards.length === establishmentLimit.value;
  } catch (err) {
    if (ac.signal.aborted) return;
    console.error("loadNearbyEstablishments error", err);
  } finally {
    if (!ac.signal.aborted) {
      establishmentLoadingMore.value = false;
      if (page === 1) nearFirstLoadPending.value = false;
    }
  }
}

async function loadUpfestEstablishments(page = 1) {
  const search = pendingSearch;
  upfestEstAc?.abort();
  const ac = (upfestEstAc = new AbortController());

  try {
    upfestEstablishmentLoadingMore.value = true;
    upfestEstablishmentError.value = null;
    if (page === 1) {
      upfestEstablishmentPage.value = 1;
      upfestEstablishmentHasMore.value = false;
    }

    const shouldRebase = page === 2 && !upfestEstablishmentsLoaded.value;
    const responses = await Promise.all(
      (shouldRebase ? [1, 2] : [page]).map((targetPage) => {
        const params = buildEstablishmentParams(
          targetPage,
          HOME_DISCOVERY_LIMIT,
        );
        return $fetch<HomeEstablishmentsResponse>(
          `/bff/establishments?${params.toString()}`,
          { signal: ac.signal },
        );
      }),
    );
    const response = responses.at(-1);
    const cards = (response?.establishments ?? []) as UiCard[];

    if (shouldRebase) {
      const firstPageCards = (responses[0]?.establishments ?? []) as UiCard[];
      const firstPageIds = new Set(firstPageCards.map((card) => card.id));
      upfestEstablishments.value = [
        ...firstPageCards,
        ...cards.filter((card) => !firstPageIds.has(card.id)),
      ];
    } else if (page === 1) {
      upfestEstablishments.value = cards;
    } else {
      const existingIds = new Set(
        upfestEstablishments.value.map((card) => card.id),
      );
      upfestEstablishments.value = [
        ...upfestEstablishments.value,
        ...cards.filter((card) => !existingIds.has(card.id)),
      ];
    }

    upfestEstablishmentsLoaded.value = true;
    upfestEstablishmentPage.value = page;
    upfestEstablishmentHasMore.value = Boolean(response?.hasMore);
    if (
      page === 1 &&
      search &&
      search === pendingSearch &&
      !ac.signal.aborted
    ) {
      emit("search_results_loaded", { ...search, result_count: cards.length });
      pendingSearch = undefined;
    }
  } catch (error) {
    if (ac.signal.aborted) return;
    console.error("loadUpfestEstablishments error", error);
    upfestEstablishmentError.value = error;
  } finally {
    if (!ac.signal.aborted) upfestEstablishmentLoadingMore.value = false;
  }
}

const onSeeAll = (sectionKey: string) => {
  // pass the hero search params along and navigate to app search
  const p = new URLSearchParams();
  p.set("rail", sectionKey);
  p.set("mode", mode.value);
  if (heroLocation.value) p.set("where", heroLocation.value);
  if (heroDate.value) p.set("when", heroDate.value);
  if (heroMiddle.value) p.set("what", heroMiddle.value);
  if (heroLat.value !== null && heroLon.value !== null) {
    p.set("lat", String(heroLat.value));
    p.set("lon", String(heroLon.value));
  }
  if (heroCountry.value) p.set("country", heroCountry.value);

  navigateTo(`${localePath("/search")}?${p.toString()}`);
};

const onFooterPrimary = () => {
  window.location.href = `${APP_URL}/#/auth/register`;
};
const onFooterSecondary = () => {
  window.location.href = `${APP_URL}/`;
};

useSeoMeta({
  title: () => t("home.seoTitle"),
  description: () => t("home.seoDescription"),
  ogTitle: () => t("home.seoTitle"),
  ogDescription: () => t("home.seoDescription"),
  ogImage: `${SITE_URL}/images/hero.jpg`,
  ogUrl: homepageUrl,
  ogType: "website",
  ogSiteName: "Upfest",
  twitterCard: "summary_large_image",
  twitterTitle: () => t("home.seoTitle"),
  twitterDescription: () => t("home.seoDescription"),
  twitterImage: `${SITE_URL}/images/hero.jpg`,
});

useHead({
  link: [
    { rel: "canonical", href: homepageUrl },
    { rel: "alternate", hreflang: "en", href: SITE_URL },
    { rel: "alternate", hreflang: "de", href: `${SITE_URL}/de` },
    { rel: "alternate", hreflang: "x-default", href: SITE_URL },
  ],
});
</script>

<template>
  <NuxtLayout>
    <div class="min-h-screen bg-white">
      <AppHeader
        :mode="mode"
        @update:mode="onModeChange"
        @search-click="onHeaderSearchClick"
        @menu-click="onHeaderMenuClick"
        @login="onHeaderLogin"
        @how-it-works-click="onHowItWorks"
      />

      <PrivacyPreferencesModal
        v-model:analytics-enabled="analyticsEnabled"
        v-model:location-enabled="locationEnabled"
        :visible="showPrivacyPreferencesModal"
        @only-necessary="keepOnlyNecessaryPrivacyPreferences"
        @save="savePrivacyPreferences"
        @accept-all="acceptAllPrivacyPreferences"
      />
      <section class="w-full px-4 pt-6">
        <div class="mx-auto w-full max-w-[1366px]">
          <HeroHeader
            v-model:location="heroLocation"
            v-model:middle-value="heroMiddle"
            v-model:date="heroDate"
            :mode="mode"
            :title-before="$t('hero.titleBefore')"
            :title-accent="$t('hero.titleAccent')"
            :subtitle="$t('hero.subtitle')"
            :lat="heroLat"
            :lon="heroLon"
            :radius-km="heroRadiusKm"
            :event-options="eventOptions"
            :establishment-options="establishmentOptions"
            @search="onHeroSearch"
            @select-location="setHeroLocation"
          />
          <div
            v-if="categoriesLoading"
            class="mt-3 flex flex-wrap gap-2"
            aria-label="Loading filters"
          >
            <div class="h-8 w-24 animate-pulse rounded-full bg-gray-200"></div>
            <div class="h-8 w-32 animate-pulse rounded-full bg-gray-200"></div>
            <div class="h-8 w-20 animate-pulse rounded-full bg-gray-200"></div>
          </div>
        </div>
      </section>

      <section class="mx-auto flex min-h-96 max-w-6xl flex-col px-4 pb-10">
        <ClientOnly>
          <div v-if="clientReady">
            <div
              v-if="mode === 'event' && eventsError && !nearRailLoading"
              class="py-6 text-sm text-red-500"
            >
              {{ $t("home.failedToLoadEvents") }}
            </div>

            <CardRail
              data-testid="nearby-discovery"
              :title="nearRailTitle"
              :items="nearItems"
              rail-key="popular"
              :has-more="nearHasMore"
              :loading="nearRailLoading"
              :item-component="
                mode === 'establishment' ? EstablishmentCard : undefined
              "
              @see-all="onSeeAll"
              @load-more="onNearLoadMore"
            >
              <template #empty>
                <h3 class="text-lg font-semibold text-indigo-950">
                  {{
                    mode === "event"
                      ? $t("home.noEventsNearYou")
                      : $t("home.noPlacesFound")
                  }}
                </h3>
                <p class="text-sm">
                  {{
                    mode === "event"
                      ? $t("home.noEventsNearYouHint")
                      : $t("home.noPlacesFoundHint")
                  }}
                </p>
                <button
                  class="btn-primary mt-3 px-4 py-2"
                  @click="onSeeAll('popular')"
                >
                  {{
                    mode === "event"
                      ? $t("home.browseAllEvents")
                      : $t("home.browseAllPlaces")
                  }}
                </button>
              </template>
            </CardRail>

            <div
              v-if="nearItems.length > 0 || upfestItems.length > 0"
              class="my-6 w-full"
              aria-label="Advertisement"
              role="complementary"
            >
              <!-- <span class="text-[10px] uppercase tracking-wider text-gray-400"
                >Sponsored</span
              >
              <div class="mx-auto mt-1 max-w-3xl">
                <AdsAdUnit slot-id="HOME_MID_FEED_SLOT_ID" />
              </div> -->
            </div>
          </div>
          <template #fallback>
            <CardRail
              :title="nearRailTitle"
              :items="[]"
              :loading="true"
              :item-component="
                mode === 'establishment' ? EstablishmentCard : undefined
              "
            />
          </template>
        </ClientOnly>

        <div
          v-if="upfestError && !upfestRailLoading"
          class="py-6 text-sm text-red-500"
          role="status"
        >
          {{ $t("home.failedToLoadDiscovery") }}
        </div>

        <CardRail
          data-testid="upfest-discovery"
          :title="upfestRailTitle"
          :items="upfestItems"
          rail-key="upfest"
          :has-more="upfestHasMore"
          :loading="upfestRailLoading"
          :see-all-to="upfestSeeAllTo"
          :item-component="
            mode === 'establishment' ? EstablishmentCard : undefined
          "
          @see-all="onSeeAll"
          @load-more="onUpfestLoadMore"
        >
          <template #empty>
            <h3 class="text-lg font-semibold text-indigo-950">
              {{
                mode === "event"
                  ? $t("home.noEventsOnUpfest")
                  : $t("home.noPlacesOnUpfest")
              }}
            </h3>
            <p class="text-sm">
              {{ $t("home.tryBroadeningSearch") }}
            </p>
          </template>
        </CardRail>
      </section>

      <section class="w-full px-4 pb-12">
        <div class="mx-auto w-full max-w-[1366px]">
          <FooterMainPage
            @primary="onFooterPrimary"
            @secondary="onFooterSecondary"
          />
        </div>
      </section>

      <div
        v-if="consentToastVisible"
        class="pointer-events-none fixed inset-x-0 bottom-4 z-[75] flex justify-center px-4"
        aria-live="polite"
        role="status"
      >
        <div
          class="max-w-md rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-[0_16px_36px_-24px_rgba(146,64,14,0.45)]"
        >
          {{ consentToastMessage }}
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>
